import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Container,
  InputAdornment,
  Tabs,
  Tab,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { invitationService } from '../utils/apiService';
import InviteMemberDialog from '../components/team-members/InviteMemberDialog';
import TeamMembersTable from '../components/team-members/TeamMembersTable';
import { useTranslation } from '../hooks/useTranslation';

const TeamMembers = () => {
  const { currentUser, currentTenant } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [emailPreviewUrl, setEmailPreviewUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
    const { t } = useTranslation();

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredInvitations = invitations.filter(invitation => {
    if (!invitation) return false;
    
    // Tab-based filter
    const matchesTab = 
      activeTab === 0 || // All tab
      (activeTab === 1 && invitation.status === 'pending') || // Pending tab
      (activeTab === 2 && invitation.status === 'accepted') || // Accepted tab
      (activeTab === 3 && invitation.status === 'expired'); // Expired tab
    
    return matchesTab;
  });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchInvitations();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [page, rowsPerPage, searchQuery]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await invitationService.getInvitations(page + 1, rowsPerPage, searchQuery);
      
      const invitationsData = Array.isArray(response) ? response : (response.invitations || response.data);
      
      if (invitationsData && Array.isArray(invitationsData)) {
        // Map _id to id if needed for backward compatibility
        const normalizedInvitations = invitationsData.map(inv => ({
          ...inv,
          id: inv.id || inv._id // Use id if available, fallback to _id
        }));
        console.log('Fetched invitations:', normalizedInvitations);
        setInvitations(normalizedInvitations);
        setTotalCount(response.total || response.totalCount || invitationsData.length);
      } else {
        console.error('Invalid invitations data:', response);
        setInvitations([]);
        setTotalCount(0);
        setError('Invalid invitation data received. Please try again.');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Failed to load invitations. Please try again.');
      setInvitations([]);
      setTotalCount(0);
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSendInvitation = async ({ email, role, permissions }) => {
    try {
      setActionLoading(true);
      setError('');
      
      const response = await invitationService.createInvitation({
        email,
        role,
        permissions
      });
      
      // Normalize the invitation response
      const normalizedInvitation = {
        ...response.invitation,
        id: response.invitation.id || response.invitation._id
      };
      
      setInvitations([normalizedInvitation, ...invitations]);
      
      if (response.invitation.emailPreviewUrl) {
        setEmailPreviewUrl(response.invitation.emailPreviewUrl);
        setSuccess('Invitation sent successfully. Click the "View Email" button to see the preview.');
      } else {
        setSuccess('Invitation sent successfully.');
      }
      
      setActionLoading(false);
      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send invitation');
      setActionLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      setActionLoading(true);
      
      console.log('Resending invitation with ID:', invitationId);
      const response = await invitationService.resendInvitation(invitationId);
      
      // Normalize the invitation response
      const normalizedInvitation = {
        ...response.invitation,
        id: response.invitation.id || response.invitation._id
      };
      
      const updatedInvitations = invitations.map(invitation => 
        invitation.id === invitationId ? normalizedInvitation : invitation
      );
      
      setInvitations(updatedInvitations);
      
      if (response.invitation.emailPreviewUrl) {
        setEmailPreviewUrl(response.invitation.emailPreviewUrl);
        setSuccess('Invitation resent successfully. Click the "View Email" button to see the preview.');
      } else {
        setSuccess('Invitation resent successfully.');
      }
      
      setActionLoading(false);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to resend invitation');
      setActionLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      setActionLoading(true);
      
      console.log('Cancelling invitation with ID:', invitationId);
      await invitationService.cancelInvitation(invitationId);
      
      const updatedInvitations = invitations.filter(
        invitation => invitation.id !== invitationId
      );
      
      setInvitations(updatedInvitations);
      setSuccess('Invitation cancelled successfully');
      setActionLoading(false);
    } catch (error) {
      console.error('Cancel error:', error);
      setError(error.response?.data?.message || 'Failed to cancel invitation');
      setActionLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setError('');
    setSuccess('');
  };
  
  const handleOpenEmailPreview = () => {
    if (emailPreviewUrl) {
      window.open(emailPreviewUrl, '_blank');
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
              {t('Team_Members.Team_Members')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
             {t('Team_Members.Manage_your_team_and_send_invitations')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          size="small"
          onClick={handleOpenDialog}
          disabled={actionLoading}
          sx={{ 
            borderRadius: '4px',
            textTransform: 'none',
            py: 0.5,
            px: 1,
          }}
        >
            {t('Team_Members.Invite_Member')}
        </Button>
      </Box>
      
      <Tabs 
        value={activeTab} 
        onChange={(event, newValue) => setActiveTab(newValue)}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label={t('Team_Members.All_Invitations')} />
  <Tab label={t('Team_Members.Pending')} />
  <Tab label={t('Team_Members.Accepted')} />
  <Tab label={t('Team_Members.Expired')} />
      </Tabs>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder={t('Team_Members.Search_by_email_or_role')}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: '100%',
              maxWidth: 500,
              height: 28,
              '& .MuiOutlinedInput-root': {
                borderRadius: '2px',
                height: 28,
              },
              '& .MuiInputBase-input': {
                py: 0.5,
              }
            }}
          />
        </Box>
      </Box>

      <TeamMembersTable 
        invitations={filteredInvitations}
        loading={loading}
        onResend={handleResendInvitation}
        onCancel={handleCancelInvitation}
        actionLoading={actionLoading}
      />

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          '& .MuiTablePagination-select': {
            py: 0.5,
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
          }
        }}
      />

      <InviteMemberDialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        onSendInvitation={handleSendInvitation}
        loading={actionLoading}
      />

      {error && (
        <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseAlert}>
          <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}

      {success && (
        <Snackbar 
          open={!!success} 
          autoHideDuration={10000} 
          onClose={handleCloseAlert}
          action={
            emailPreviewUrl ? (
              <Button 
                color="secondary" 
                size="small" 
                onClick={handleOpenEmailPreview}
                sx={{ textTransform: 'none', fontWeight: 'medium' }}
              >
               {t('Team_Members.View_Email')}
              </Button>
            ) : null
          }
        >
          <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
      )}
    </Container>
  );
};

export default TeamMembers;
